<template>
  <div
    v-if="showModal"
    class="fixed inset-0 z-50 flex items-center justify-center mt-5 overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
  >
    <div class="relative w-full h-full p-4 md:w-6/12 md:h-auto">
      <div class="relative bg-white rounded-lg shadow">
        <div class="flex items-center justify-between px-6 py-4 lg:px-8">
          <h1 class="font-medium">More Filters</h1>
          <button
            type="button"
            class="flex justify-end text-gray-500 bg-transparent w-14 hover:text-gray-900"
            @click="closeViewModal"
            data-modal-toggle="modal"
          >
            <svg
              aria-hidden="true"
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span class="sr-only">Close modal</span>
          </button>
        </div>
        <hr />
        <div class="flex flex-col px-6 py-6 lg:px-8">
          <!-- Furnishing Section -->
          <div class="flex flex-col">
            <div class="flex items-center">
              <BedSvg color="black" height="6" width="6" />
              <p class="pl-2 font-medium">Furnishing</p>
            </div>
            <div class="flex flex-wrap gap-3 my-4">
              <button
                @click="selectFurnishing('all')"
                :class="{
                  'bg-primary text-white': selectedFurnishing === 'all',
                }"
                class="bg-[#F7F6FB] border border-primary rounded-lg text-sm p-1 w-auto px-2 font-light h-8 flex items-center justify-center"
              >
                All furnishings
              </button>
              <button
                @click="selectFurnishing('furnished')"
                :class="{
                  'bg-primary text-white': selectedFurnishing === 'furnished',
                }"
                class="flex items-center justify-center w-auto h-8 p-1 px-2 text-sm font-light text-black border rounded-lg border-stone-200"
              >
                Furnished
              </button>
              <button
                @click="selectFurnishing('unfurnished')"
                :class="{
                  'bg-primary text-white': selectedFurnishing === 'unfurnished',
                }"
                class="flex items-center justify-center w-auto h-8 p-1 px-2 text-sm font-light text-black border rounded-lg border-stone-200"
              >
                Unfurnished
              </button>
              <button
                @click="selectFurnishing('partlyFurnished')"
                :class="{
                  'bg-primary text-white':
                    selectedFurnishing === 'partlyFurnished',
                }"
                class="flex items-center justify-center w-auto h-8 p-1 px-2 text-sm font-light text-black border rounded-lg border-stone-200"
              >
                Partly Furnished
              </button>
            </div>
          </div>
          <hr />
          <!-- Property Size Section -->
          <div class="flex flex-col mt-4">
            <div class="flex items-center">
              <PropertySizeSvg color="black" height="5" width="5" />
              <p class="pl-2 font-medium">Property Size (Sqft)</p>
            </div>
            <div class="flex items-center mt-4 mb-4 md:w-96">
              <div class="w-auto md:pr-2 md:w-1/2">
                <input
                  type="text"
                  class="md:w-full w-36 placeholder:text-gray-500 h-12 px-2 py-1 mb-2 text-black border border-gray-400 rounded-lg focus:outline-none focus:border-[#8b86a0]"
                  v-model="min_area"
                  placeholder="Min. Area"
                />
              </div>
              <hr class="bg-color" />
              <div class="w-auto md:pl-2 md:w-1/2">
                <input
                  type="text"
                  class="md:w-full w-36 h-12 placeholder:text-gray-500 px-2 py-1 mb-2 text-black border border-gray-400 rounded-lg focus:outline-none focus:border-[#8b86a0]"
                  v-model="max_area"
                  placeholder="Max. Area"
                />
              </div>
            </div>
          </div>
          <hr />
          <!-- Amenities Section -->
          <div class="flex flex-col mt-4">
            <div class="flex items-center">
              <AmenitiesSvg color="black" height="6" width="6" />
              <p class="pl-2 font-medium">Amenities</p>
            </div>
            <div class="grid grid-cols-3 gap-5 mt-4">
              <div
                class="flex items-center mb-4"
                v-for="amenity in amenities"
                :key="amenity.id"
              >
                <input
                  :id="'amenity-checkbox-' + amenity.id"
                  type="checkbox"
                  :value="amenity.id"
                  v-model="amenityId"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label
                  :for="'amenity-checkbox-' + amenity.id"
                  class="text-black ms-2"
                >
                  {{ amenity.name }}
                </label>
              </div>
            </div>
          </div>
          <hr />
          <!-- Keywords Section -->
          <div class="flex flex-col mt-4">
            <div class="flex items-center">
              <SearchSvg color="black" width="5" height="5" stroke="#000" />
              <p class="pl-2 font-medium">Keywords</p>
            </div>
            <div>
              <input
                type="text"
                class="w-full h-12 p-2 mt-4 text-black border border-gray-400 rounded-lg placeholder:text-gray-500"
                placeholder="Keywords: e.g. beach, freezer"
                v-model="keyword"
              />
            </div>
          </div>
          <hr class="my-4" />
          <div class="flex justify-end">
            <button
              class="w-auto h-12 px-3 text-white rounded-lg bg-primary"
              @click="propertyFilters"
            >
              Show results
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showModal" class="fixed inset-0 z-40 bg-black opacity-25"></div>
</template>

<script>
import { ref, onMounted } from "vue";
import SearchSvg from "@/components/svg/SearchSvg.vue";
import PropertySizeSvg from "@/components/svg/PropertySizeSvg.vue";
import axios from "axios";
import { useStore } from "vuex";
import BedSvg from "@/components/svg/BedSvg.vue";
import AmenitiesSvg from "@/components/svg/AmenitiesSvg.vue";

export default {
  components: {
    PropertySizeSvg,
    SearchSvg,
    BedSvg,
    AmenitiesSvg,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["close-modal", "more-property-filters"],
  setup(props, { emit }) {
    const selectedFurnishing = ref("all");
    const amenities = ref([]);
    const amenityId = ref([]);
    const keyword = ref("");
    const min_area = ref("");
    const max_area = ref("");
    const store = useStore();
    const base_url = store.state.baseUrl;

    onMounted(() => {
      axios
        .get(`${base_url}amenities`)
        .then((response) => {
          amenities.value = response.data.data;
        })
        .catch((error) => console.log(error));
    });

    const selectFurnishing = (furnishing) => {
      selectedFurnishing.value = furnishing;
    };

    const closeViewModal = () => {
      emit("close-modal");
    };

    const propertyFilters = () => {
      const filteredAmenities = amenityId.value.filter((id) => id !== null);
      const furnishingStatusValue =
        selectedFurnishing.value === "all" ? "" : selectedFurnishing.value;

      emit("more-property-filters", {
        keyword: keyword.value,
        min_area: min_area.value,
        max_area: max_area.value,
        amenity_id: filteredAmenities,
        furnishing_status: furnishingStatusValue,
      });
      closeViewModal();
    };

    return {
      amenities,
      amenityId,
      keyword,
      min_area,
      max_area,
      closeViewModal,
      propertyFilters,
      selectedFurnishing,
      selectFurnishing,
    };
  },
};
</script>

<style>
.bg-color {
  background-color: #b0b0b0;
  margin: 0 1.2rem;
  width: 1.6rem;
  box-sizing: content-box;
  overflow: visible;
}
</style>
